import classNames from 'classnames';
import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { opinionCounterpartyTypeOptions } from '../../constants/entity';
import { Dropdown, DropdownOption } from '../../shared/dropdown/Dropdown';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { getAllDropdownLists } from '../dropdown-lists/store';
import { fetchOpinionSubCounterpartyTypesStarted, fetchOpinionSubCounterpartyTypesSuccessful, getJurisdictionSubCounterpartyTypes, selectSubCounterpartyTypesJurisdiction, selectSubCounterpartyTypesParent, getIsEditingSelectedSubCounterparty, getSubCounterpartyScopeFilters } from '../sub-counterparty-types/store';
import styles from './SubCounterpartyTypes.module.scss';
import { OpinionScope } from '../opinions/store';

interface SubCounterpartyJurisdictionSectionProps {
    parentOptionsHeight: number;
    jurisdiction: string | null;
    parentCounterparty: string | null;
}

export const SubCounterpartyJurisdictionSection: React.FC<SubCounterpartyJurisdictionSectionProps> = ({ parentOptionsHeight, jurisdiction, parentCounterparty }) => {
    const dispatch = useAppDispatch();

    const dropdownLists = useAppSelector(getAllDropdownLists);
    const jurisdictionSubCounterpartyTypes = useAppSelector(getJurisdictionSubCounterpartyTypes);
    const isEditing = useAppSelector(getIsEditingSelectedSubCounterparty);
    const selectedScopeFilters = useAppSelector(getSubCounterpartyScopeFilters);

    const scopeFilters = useMemo(() => selectedScopeFilters.length === 0 ? Object.values(OpinionScope) : selectedScopeFilters, [selectedScopeFilters]);

    const setJurisdiction = useCallback((value: string | null) => {
        dispatch(selectSubCounterpartyTypesJurisdiction(value));
        if (!isNull(value)) {
            dispatch(fetchOpinionSubCounterpartyTypesStarted(value));
        }
    }, [dispatch]);

    const setParentCounterparty = useCallback((value: string) => {
        dispatch(selectSubCounterpartyTypesParent(value));
    }, [dispatch]);

    const getOptionCount = useCallback((option: string) => jurisdictionSubCounterpartyTypes.filter(({ parentCounterpartyType, scopes }) => (parentCounterpartyType === option) && (scopes.some(scope => scopeFilters.includes(scope)))).length, [jurisdictionSubCounterpartyTypes, scopeFilters]);

    const jurisdictionOptions = dropdownLists.find(({ name }) => name === 'OpinionJurisdiction')?.options.map(type => ({ value: type, label: type })) || [];
    const jurisdictionValue = jurisdictionOptions.filter(option => option.value === jurisdiction) || null;

    const updateJurisdiction = useCallback((option: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(option)) {
            value = (option as DropdownOption).value;
        } else {
            dispatch(fetchOpinionSubCounterpartyTypesSuccessful([]));
            dispatch(selectSubCounterpartyTypesParent(null));
        }
        setJurisdiction(value);
    }, [dispatch, setJurisdiction]);

    return (
        <div className={styles.subCounterpartyJurisdictionSectionWrapper}>
            <div className={styles.dropdownWrapper}>
                <div className={styles.dropdownLabel}>Jurisdiction</div>
                <Dropdown
                    options={jurisdictionOptions}
                    value={jurisdictionValue}
                    onChange={updateJurisdiction}
                    disabled={isEditing}
                    cursor={isEditing ? 'not-allowed' : 'auto'}
                />
            </div>
            {jurisdiction && <div style={{ height: parentOptionsHeight, marginTop: '20px' }}>
                <Scrollable>
                    {opinionCounterpartyTypeOptions.map((option, index) => {
                        const counter = getOptionCount(option);
                        return (
                            <div key={index} onClick={() => setParentCounterparty(option)} className={classNames(styles.parentCounterpartyButton, { [styles.selectedButton]: option === parentCounterparty })}>{option}{counter > 0 ? ` (${counter})` : ''}</div>
                        );
                    }
                    )}
                </Scrollable>
            </div>}
        </div>
    );
};
